import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import CodePreview from "@/components/CodePreview";
import StatementPreview from "@/components/StatementPreview";
import { Upload, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EXAMPLE_CODE = [
  "// codira ad studio",
  "const ads = 100;",
  "for (let i = 0; i < ads; i++) {",
  "  codira.ship();",
  "}",
];
const EXAMPLE_STMT = {
  headline: "Nine specialized AI agents.",
  accent: "AI",
  bullets: [
    "Three layers of style context the agents read on every run.",
    "Five deterministic patch guards. Three editing modes.",
  ],
};

// Common ad sizes across networks (see docs/AD_SIZES.md). Upload a background SVG
// for any of these — or enter a custom size. Backend accepts any 200–8000 px size.
const SIZE_PRESETS = [
  { label: "Square", aspect: "1:1", width: 1080, height: 1080, note: "Meta/LinkedIn/X feed" },
  { label: "Portrait", aspect: "4:5", width: 1080, height: 1350, note: "Meta feed (max reach)" },
  { label: "Story / Reel", aspect: "9:16", width: 1080, height: 1920, note: "Stories · TikTok · Shorts" },
  { label: "Landscape", aspect: "16:9", width: 1920, height: 1080, note: "YouTube · X · LinkedIn" },
  { label: "Link", aspect: "1.91:1", width: 1200, height: 628, note: "Meta link · responsive" },
  { label: "Tall Pin", aspect: "2:3", width: 1000, height: 1500, note: "Pinterest" },
];

// Proportional default content box (matches the seeded 1:1 card geometry). It's a
// starting point — the uploader can nudge the x/y/w/h to fit their own card art.
function defaultBox(w, h) {
  return {
    x: Math.round(0.093 * w),
    y: Math.round(0.113 * h),
    width: Math.round(0.815 * w),
    height: Math.round(0.62 * h),
  };
}

function aspectLabel(w, h) {
  const preset = SIZE_PRESETS.find((p) => p.width === w && p.height === h);
  if (preset) return preset.aspect;
  const g = (a, b) => (b ? g(b, a % b) : a);
  const d = g(w, h) || 1;
  return `${w / d}:${h / d}`;
}

function UploadModal({ onClose, onUploaded }) {
  const [name, setName] = useState("");
  const [width, setWidth] = useState(1080);
  const [height, setHeight] = useState(1080);
  const [custom, setCustom] = useState(false);
  const [box, setBox] = useState(defaultBox(1080, 1080));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  // Set the size (and reset the content box to the proportional default).
  const setSize = (w, h) => { setWidth(w); setHeight(h); setBox(defaultBox(w, h)); };
  const isActive = (w, h) => !custom && width === w && height === h;

  const submit = async () => {
    if (!file) { toast.error("Choose an SVG file"); return; }
    if (!name.trim()) { toast.error("Name your template"); return; }
    if (width < 200 || height < 200 || width > 8000 || height > 8000) {
      toast.error("Size must be 200–8000 px on each side"); return;
    }
    setBusy(true);
    try {
      const meta = {
        name: name.trim(), aspect: aspectLabel(width, height),
        width, height, content_box: box, styling: {},
      };
      const fd = new FormData();
      fd.append("file", file);
      fd.append("meta", JSON.stringify(meta));
      await api.post("/templates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template uploaded");
      onUploaded();
      onClose();
    } catch (e) {
      toast.error("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white rounded-2xl p-8 relative my-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-900">
            <X className="h-5 w-5" />
          </button>
          <h2 className="font-lexend text-2xl text-gray-900">Upload statement background</h2>
          <p className="text-sm text-gray-500 mt-1">
            A flat SVG with your baked card + footer lockup. Upload one per size — the
            headline + checklist render (in Inter) inside the content box.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Statement — Feature launch"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Size</label>
              <div className="grid grid-cols-2 gap-2">
                {SIZE_PRESETS.map((p) => (
                  <button key={p.aspect + p.width} onClick={() => { setCustom(false); setSize(p.width, p.height); }}
                    className={`rounded-lg border px-3 py-2 text-left text-sm ${
                      isActive(p.width, p.height) ? "border-brand bg-brand-50" : "border-gray-200 hover:border-gray-300"
                    }`}>
                    <span className={`block font-medium ${isActive(p.width, p.height) ? "text-brand" : "text-gray-900"}`}>
                      {p.label} · {p.aspect}
                    </span>
                    <span className="block text-[11px] text-gray-500">{p.width}×{p.height} — {p.note}</span>
                  </button>
                ))}
                <button onClick={() => setCustom(true)}
                  className={`rounded-lg border px-3 py-2 text-left text-sm ${
                    custom ? "border-brand bg-brand-50" : "border-gray-200 hover:border-gray-300"
                  }`}>
                  <span className={`block font-medium ${custom ? "text-brand" : "text-gray-900"}`}>Custom size</span>
                  <span className="block text-[11px] text-gray-500">Any 200–8000 px</span>
                </button>
              </div>
            </div>

            {custom && (
              <div className="grid grid-cols-2 gap-3">
                <label className="text-[11px] text-gray-500">Width (px)
                  <input type="number" value={width}
                    onChange={(e) => setSize(Number(e.target.value) || 0, height)}
                    className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm mt-0.5" />
                </label>
                <label className="text-[11px] text-gray-500">Height (px)
                  <input type="number" value={height}
                    onChange={(e) => setSize(width, Number(e.target.value) || 0)}
                    className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm mt-0.5" />
                </label>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Content box <span className="text-gray-400 font-normal">— where text renders (px); adjust to fit your card</span>
              </label>
              <div className="grid grid-cols-4 gap-2">
                {["x", "y", "width", "height"].map((k) => (
                  <label key={k} className="text-[10px] text-gray-500">
                    {k}
                    <input type="number" value={box[k]}
                      onChange={(e) => setBox((b) => ({ ...b, [k]: Number(e.target.value) }))}
                      className="w-full rounded border border-gray-200 px-2 py-1 text-sm mt-0.5" />
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">SVG file</label>
              <input ref={inputRef} type="file" accept=".svg,image/svg+xml"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:text-brand file:px-3 file:py-1.5 file:text-sm" />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            <button onClick={submit} disabled={busy} className="btn-brand inline-flex items-center gap-2 px-5 py-2 text-sm disabled:opacity-40">
              <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Templates() {
  const [items, setItems] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = () => api.get("/templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    try { await api.delete(`/templates/${t.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  const byAspect = (a, b) => a.width / a.height - b.width / b.height;
  const code = items.filter((t) => (t.kind || "code") === "code").sort(byAspect);
  const statement = items.filter((t) => t.kind === "statement").sort(byAspect);

  const Card = ({ t }) => (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
      <div className="aspect-square bg-gray-50 p-3 flex items-center justify-center">
        <div className="w-full" style={{ maxWidth: t.height > t.width ? `${(t.width / t.height) * 100}%` : "100%" }}>
          {t.kind === "statement"
            ? <StatementPreview template={t} headline={EXAMPLE_STMT.headline} accent={EXAMPLE_STMT.accent} bullets={EXAMPLE_STMT.bullets} />
            : <CodePreview template={t} codeLines={EXAMPLE_CODE} />}
        </div>
      </div>
      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{t.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t.width}×{t.height} · {t.aspect} · {t.duration_seconds}s</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-medium text-brand bg-brand-50 px-2 py-0.5 rounded">{t.aspect}</span>
          {t.bg_svg_path && (
            <button onClick={() => remove(t)} title="Delete" className="text-gray-300 hover:text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8 space-y-10 max-w-[1500px]">
      <div>
        <h1 className="text-3xl font-lexend text-gray-900">Templates</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Two ad families across the same canvas sizes. <span className="font-medium">Code ads</span> type animated code
          inside the dark card; <span className="font-medium">statement cards</span> reveal a headline + checklist over an
          uploaded background. Code sizes are fixed; statement backgrounds can be uploaded.
        </p>
      </div>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Code ads</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {code.map((t) => <Card key={t.id} t={t} />)}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900">Statement cards</h2>
          <button onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-brand text-brand hover:bg-brand hover:text-white px-3 py-1.5 text-sm font-medium transition-colors">
            <Upload className="h-4 w-4" /> Upload SVG
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statement.map((t) => <Card key={t.id} t={t} />)}
        </div>
      </section>

      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} onUploaded={load} />}
    </div>
  );
}
